/**
 * Sanitizes raw markdown content before it reaches the markdown-it block tokenizer.
 *
 * Two constructs cause `ParserBlock.prototype.tokenize` to enter catastrophic
 * backtracking / exponential rule-matching, causing OOM browser crashes:
 *
 * ## 1. Tables indented 2+ spaces (inside a list item)
 *
 * The `markdown-it-table` plugin requires the table header row to start at
 * column 0 (or ≤1 space of indentation). When an AI response places a table
 * inside a list item — a common pattern — the table rows are indented with
 * 2+ spaces and are never recognized as a table block.
 *
 * Instead, each `|---` separator row triggers markdown-it's `lheading`
 * (setext heading) rule which checks whether the preceding paragraph can be
 * promoted to a heading. This check is O(n) against all prior paragraphs,
 * repeated for every `|` row in the table → O(n²) overall. For documents
 * with 200+ lines and multiple tables, this crashes the browser.
 *
 * ## 2. Code fences indented 4+ spaces (inside nested list items)
 *
 * markdown-it's `fence` block rule only recognizes fences with 0–3 spaces of
 * leading indentation. Fences indented with 4+ spaces (common inside nested
 * list items like "     ```jql") are treated as indented code continuation,
 * meaning the opening fence is never paired with its closing fence. The block
 * parser then tries every rule against the remaining content until EOF →
 * effectively infinite loop → OOM.
 *
 * ## Fix
 *
 * Strip excess leading whitespace from table rows and fence delimiters so
 * they are recognized by the parser as valid block-level constructs. A
 * fence-tracking state machine ensures that table rows and fence delimiters
 * *inside* a code fence are never modified.
 */
// Fast pre-check: matches a line starting with 2+ spaces followed by `|` or ` ``` `.
// Uses (^|\n) so it also catches indented constructs on the very first line.
// If the content has neither, skip the full line-by-line pass entirely.
const NEEDS_SANITIZE_RE = /(?:^|\n) {2,}[|`]/;

export function sanitizeMarkdown(content: string): string {
	if (!NEEDS_SANITIZE_RE.test(content)) {
		return content;
	}

	const lines = content.split('\n');
	const result: string[] = [];
	let inFence = false;
	let fenceIndent = 0;

	for (const line of lines) {
		const stripped = line.replace(/^\s+/, '');
		const indent = line.length - stripped.length;

		if (!inFence && stripped.startsWith('```')) {
			if (indent >= 4) {
				// Outdent deeply-indented opening fence to column 0 so the parser
				// recognizes it as a fenced code block
				result.push(stripped);
				inFence = true;
				fenceIndent = indent;
			} else {
				result.push(line);
				inFence = true;
				fenceIndent = indent;
			}
		} else if (inFence && stripped.startsWith('```')) {
			if (indent >= 4 && fenceIndent >= 4) {
				// Outdent matching closing fence
				result.push(stripped);
			} else {
				result.push(line);
			}
			inFence = false;
			fenceIndent = 0;
		} else if (!inFence && indent >= 2 && stripped.startsWith('|')) {
			// Outdent table rows that are indented inside list items so
			// markdown-it-table recognizes them as a proper table block
			result.push(stripped);
		} else {
			result.push(line);
		}
	}
	return result.join('\n');
}

// Matches a markdown table delimiter/separator row, with or without outer pipes,
// e.g. "--- | ---", "| :--- | ---: |", ":-:|:-:". Requires at least one dash.
const SEPARATOR_ROW_RE = /^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)*\|?\s*$/;

const isSeparatorRow = (line: string): boolean => line.includes('-') && SEPARATOR_ROW_RE.test(line);

const isTableRow = (line: string): boolean => line.includes('|') && line.trim().length > 0;

/**
 * Adds outer pipes to "borderless" GFM tables (rows without a leading/trailing
 * `|`) so they parse correctly.
 *
 * Under the `zero` markdown-it preset, `markdown-it-table` mis-parses borderless
 * body rows — it drops the first character of each cell. Borderless tables are
 * valid GFM and common in LLM output, so we bracket every row of a detected
 * table with pipes before tokenizing.
 *
 * A table block is a run of non-blank `|` rows whose second line is a separator
 * row; a blank line ends the block. Fenced code blocks are skipped.
 */
export function normalizeBorderlessTables(content: string): string {
	// Fast pre-check: needs at least one line with a pipe.
	if (!content.includes('|')) {
		return content;
	}

	const addOuterPipes = (line: string): string => {
		let trimmed = line.trim();
		if (!trimmed.startsWith('|')) {
			trimmed = `| ${trimmed}`;
		}
		if (!trimmed.endsWith('|')) {
			trimmed = `${trimmed} |`;
		}
		return trimmed;
	};

	const isFenceDelimiter = (line: string): boolean => line.trimStart().startsWith('```');

	const lines = content.split('\n');
	const result: string[] = [];
	let inFence = false;

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];

		// Skip fenced code blocks entirely.
		if (isFenceDelimiter(line)) {
			inFence = !inFence;
			result.push(line);
			continue;
		}

		const next = lines[i + 1];

		// A table starts on a row immediately followed by a separator.
		if (!inFence && isTableRow(line) && next !== undefined && isSeparatorRow(next)) {
			let j = i;
			// Consume contiguous, non-blank rows until the table ends.
			while (j < lines.length && lines[j].trim() !== '' && isTableRow(lines[j])) {
				result.push(addOuterPipes(lines[j]));
				j++;
			}
			i = j - 1;
		} else {
			result.push(line);
		}
	}

	return result.join('\n');
}
